import {
    debounce,
    monitorForChanges,
} from "../../components/helpers/_observer-handler";
import { isFirefox, isSafari } from "../../common/_constants";
import {
    getNavigationBreakpoint,
    getNavigationClass,
    handleNavigationMediaQueryChange,
    isNavigationV3,
} from "./breakpoints";
import { toggleEditionMenu, toggleMobileSidebarMenu } from "./mobile";

// Desktop Hover state of sports menu upon hovering more menu
const sportMenusHoverHandler = async () => {
    const moreMenu = document.querySelector("#desktopAdditionalMenu");

    if (!moreMenu) {
        return;
    }

    moreMenu.addEventListener("mouseover", () =>
        toggleSportMenusTextColor(true),
    );
    moreMenu.addEventListener("mouseout", () =>
        toggleSportMenusTextColor(false),
    );

    function toggleSportMenusTextColor(isHovered) {
        const sportsMenu = moreMenu.previousElementSibling;

        if (!sportsMenu) {
            return;
        }

        if (isHovered) {
            sportsMenu.classList.add("text-d1");
        } else {
            sportsMenu.classList.remove("text-d1");
        }
    }

    const additionalMenuColumns = document.querySelectorAll(
        "[data-additional-sub-menu-columns]",
    );

    await preSetHoverBoldText(additionalMenuColumns, "column");
};

export const enableNavigationV3HoverSync = () => {
    const navigation = document.querySelector(
        "#mainNavigation[data-navigation-version='v3']",
    );
    if (!navigation || navigation.dataset.hoverSyncBound === "true") {
        return;
    }

    const primaryMenu = navigation.querySelector("#desktopPrimaryMenu");
    const additionalMenu = navigation.querySelector("#desktopAdditionalMenu");

    if (!primaryMenu || !additionalMenu) {
        return;
    }

    navigation.dataset.hoverSyncBound = "true";

    const menuItems = [
        ...primaryMenu.querySelectorAll("[data-menu-item]"),
        ...Array.from(additionalMenu.children),
    ];
    const moreMenuButton = navigation.querySelector(
        "[data-testid='moreMenuItems'] button",
    );
    const moreMenuItem = navigation.querySelector(
        "[data-testid='moreMenuItems']",
    );
    const additionalMenuWhiteClass = getNavigationClass(
        "md:text-white",
        "lg:text-white",
    );
    const additionalMenuItemsWithWhite = Array.from(
        additionalMenu.children,
    ).filter((item) => item.classList.contains(additionalMenuWhiteClass));

    if (menuItems.length === 0) {
        return;
    }

    let activeHoverCount = 0;
    const hoveredItems = new Set();

    const setHoverState = (isHovered) => {
        menuItems.forEach((item) => {
            item.classList.toggle("text-d1", isHovered);
        });

        additionalMenuItemsWithWhite.forEach((item) => {
            if (isHovered) {
                item.classList.remove(additionalMenuWhiteClass);
            } else {
                item.classList.add(additionalMenuWhiteClass);
            }
        });

        if (moreMenuButton) {
            const shouldGreyMoreButton =
                isHovered &&
                Array.from(hoveredItems).some((item) => item !== moreMenuItem);
            moreMenuButton.classList.toggle(
                "bg-desktop-burger-menu",
                !shouldGreyMoreButton,
            );
            moreMenuButton.classList.toggle(
                "bg-desktop-burger-menu-hover",
                shouldGreyMoreButton,
            );
        }
    };

    const onMenuEnter = (event) => {
        activeHoverCount += 1;
        hoveredItems.add(event.currentTarget);
        setHoverState(true);
    };

    const onMenuLeave = (event) => {
        activeHoverCount = Math.max(0, activeHoverCount - 1);
        hoveredItems.delete(event.currentTarget);
        if (activeHoverCount === 0) {
            setHoverState(false);
        }
    };

    const bindHoverSyncListeners = () => {
        menuItems.forEach((item) => {
            item.addEventListener("mouseenter", onMenuEnter);
            item.addEventListener("mouseleave", onMenuLeave);
        });
    };

    const unbindHoverSyncListeners = () => {
        menuItems.forEach((item) => {
            item.removeEventListener("mouseenter", onMenuEnter);
            item.removeEventListener("mouseleave", onMenuLeave);
        });
        activeHoverCount = 0;
        hoveredItems.clear();
        setHoverState(false);
    };

    handleNavigationMediaQueryChange(
        unbindHoverSyncListeners,
        bindHoverSyncListeners,
    );
};

// Desktop navigation logo condensed state
let desktopLastScrollTop = 0;
let desktopNavState = "expanded";
let desktopScrollHandler = null;
let desktopWheelHandler = null;
let desktopLayoutResizeObserver = null;
const DESKTOP_SCROLL_DELTA = 6;
const DESKTOP_STATE_COOLDOWN_MS = 500;
/** After wheel/trackpad, scroll may fire; ignore scrollY delta (layout + chained scroll) briefly. */
const DESKTOP_WHEEL_TO_SCROLL_GRACE_MS = 120;
let desktopLastStateChange = 0;
let desktopLastWheelTime = 0;

const readDesktopScrollTop = () =>
    window.scrollY || document.documentElement.scrollTop;

const syncDesktopLastScrollFromWindow = () => {
    desktopLastScrollTop = readDesktopScrollTop();
};

const applyDesktopLogoState = (state) => {
    const tsnDesktopLogo = document.querySelector("#tsnLogo");
    const expandedLogoImgDiv = document.querySelector("#expanded-logo");
    const condensedLogoImgDiv = document.querySelector("#condensed-logo");
    const secondaryMenu = document.querySelector(
        "#secondaryMenu[data-navigation-version='v3']",
    );

    if (!tsnDesktopLogo || !expandedLogoImgDiv || !condensedLogoImgDiv) {
        return;
    }

    if (state === "condensed") {
        // Enable transition on the container
        tsnDesktopLogo.classList.add("nav-logo--transition");

        // Hide expanded logo smoothly
        expandedLogoImgDiv.classList.remove("logo-expanded--visible");
        expandedLogoImgDiv.classList.add("logo-expanded--hidden");

        // Show condensed logo smoothly
        condensedLogoImgDiv.classList.remove("logo-condensed--hidden");
        condensedLogoImgDiv.classList.add("logo-condensed--visible");

        if (secondaryMenu) {
            secondaryMenu.classList.add("secondary-nav-v3-condensed");
            secondaryMenu.classList.remove("secondary-nav-v3-expanded");
        }
    } else {
        // Hide condensed logo smoothly
        condensedLogoImgDiv.classList.remove("logo-condensed--visible");
        condensedLogoImgDiv.classList.add("logo-condensed--hidden");

        // Show expanded logo smoothly
        expandedLogoImgDiv.classList.remove("logo-expanded--hidden");
        expandedLogoImgDiv.classList.add("logo-expanded--visible");

        if (secondaryMenu) {
            secondaryMenu.classList.remove("secondary-nav-v3-condensed");
            secondaryMenu.classList.add("secondary-nav-v3-expanded");
        }
    }

    desktopNavState = state;
    desktopLastStateChange = performance.now();
    // Header / secondary height changes adjust scrollY without a user "scroll"; re-sync
    // after the browser applies layout so the next delta is not a false signal.
    requestAnimationFrame(() => {
        requestAnimationFrame(() => {
            syncDesktopLastScrollFromWindow();
        });
    });
};

const updateDesktopLogo = () => {
    const scrollTop = readDesktopScrollTop();
    const delta = scrollTop - desktopLastScrollTop;
    const now = performance.now();

    if (now - desktopLastStateChange < DESKTOP_STATE_COOLDOWN_MS) {
        return;
    }

    if (now - desktopLastWheelTime < DESKTOP_WHEEL_TO_SCROLL_GRACE_MS) {
        syncDesktopLastScrollFromWindow();
        return;
    }

    if (Math.abs(delta) < DESKTOP_SCROLL_DELTA) {
        return;
    }

    if (scrollTop <= 0) {
        if (desktopNavState !== "expanded") {
            applyDesktopLogoState("expanded");
        } else {
            syncDesktopLastScrollFromWindow();
        }
        return;
    }

    if (delta > 0 && desktopNavState !== "condensed") {
        applyDesktopLogoState("condensed");
    } else if (delta < 0 && desktopNavState !== "expanded") {
        applyDesktopLogoState("expanded");
    } else {
        syncDesktopLastScrollFromWindow();
    }
};

/**
 * User intent (wheel / trackpad). Does not spuriously flip when layout changes scrollY.
 * Scroll position is still read for "at top" via updateDesktopLogo on scroll.
 */
const handleDesktopNavWheel = (e) => {
    if (e.ctrlKey) {
        return;
    }
    if (Math.abs(e.deltaX) > Math.abs(e.deltaY) * 1.5) {
        return;
    }
    const now = performance.now();
    if (now - desktopLastStateChange < DESKTOP_STATE_COOLDOWN_MS) {
        return;
    }
    if (Math.abs(e.deltaY) < DESKTOP_SCROLL_DELTA) {
        return;
    }

    desktopLastWheelTime = now;

    const scrollTop = readDesktopScrollTop();
    if (scrollTop <= 0) {
        if (desktopNavState !== "expanded") {
            applyDesktopLogoState("expanded");
        } else {
            syncDesktopLastScrollFromWindow();
        }
        return;
    }

    if (e.deltaY > 0 && desktopNavState !== "condensed") {
        applyDesktopLogoState("condensed");
    } else if (e.deltaY < 0 && desktopNavState !== "expanded") {
        applyDesktopLogoState("expanded");
    } else {
        syncDesktopLastScrollFromWindow();
    }
};

const connectDesktopLayoutResizeSync = () => {
    if (typeof ResizeObserver === "undefined") {
        return;
    }
    const secondary = document.querySelector(
        "#secondaryMenu[data-navigation-version='v3']",
    );
    if (!secondary) {
        return;
    }
    desktopLayoutResizeObserver = new ResizeObserver(() => {
        requestAnimationFrame(() => {
            syncDesktopLastScrollFromWindow();
        });
    });
    desktopLayoutResizeObserver.observe(secondary);
};

const disconnectDesktopLayoutResizeSync = () => {
    if (desktopLayoutResizeObserver) {
        desktopLayoutResizeObserver.disconnect();
        desktopLayoutResizeObserver = null;
    }
};

const toggleTSNLogo = () => {
    let ticking = false;

    const scrollHandler = () => {
        if (!ticking) {
            window.requestAnimationFrame(() => {
                updateDesktopLogo();
                ticking = false;
            });

            ticking = true;
        }
    };

    updateDesktopLogo();

    return scrollHandler;
};

const enableToggleTSNLogo = () => {
    const isExpandedLogoOnly = document.querySelector("[data-expanded-logo]");
    if (isExpandedLogoOnly) return;

    desktopLastScrollTop = readDesktopScrollTop();
    applyDesktopLogoState(desktopLastScrollTop > 0 ? "condensed" : "expanded");
    desktopLastStateChange = 0;
    desktopScrollHandler = toggleTSNLogo();
    desktopWheelHandler = handleDesktopNavWheel;
    window.addEventListener("scroll", desktopScrollHandler, { passive: true });
    window.addEventListener("wheel", desktopWheelHandler, { passive: true });
    connectDesktopLayoutResizeSync();
};

const disableToggleTSNLogo = () => {
    const tsnDesktopLogo = document.querySelector("#tsnLogo");
    const isExpandedLogoOnly = document.querySelector("[data-expanded-logo]");
    const expandedLogoImgDiv = document.querySelector("#expanded-logo");
    const condensedLogoImgDiv = document.querySelector("#condensed-logo");

    if (isExpandedLogoOnly) return;

    if (
        tsnDesktopLogo &&
        condensedLogoImgDiv.classList.contains("logo-condensed--visible")
    ) {
        // Show expanded logo smoothly
        expandedLogoImgDiv.classList.remove("logo-expanded--hidden");
        expandedLogoImgDiv.classList.add("logo-expanded--visible");

        // Hide condensed logo smoothly
        condensedLogoImgDiv.classList.remove("logo-condensed--visible");
        condensedLogoImgDiv.classList.add("logo-condensed--hidden");
    }

    if (desktopNavState !== "expanded") {
        applyDesktopLogoState("expanded");
    }
    if (desktopScrollHandler) {
        window.removeEventListener("scroll", desktopScrollHandler);
        desktopScrollHandler = null;
    }
    if (desktopWheelHandler) {
        window.removeEventListener("wheel", desktopWheelHandler);
        desktopWheelHandler = null;
    }
    disconnectDesktopLayoutResizeSync();
};

export const condensedStateDesktopLogo = () => {
    handleNavigationMediaQueryChange(disableToggleTSNLogo, enableToggleTSNLogo);
};

// Desktop sub navigation height with teams handlers
const setSubNavItemsHeight = () => {
    const subNavContainers = document.querySelectorAll(
        "#desktopNavigation [data-has-dropdown]",
    );

    subNavContainers.forEach((subNavContainer) => {
        const menu = subNavContainer.querySelector("[data-sub-menu]");
        const teams = subNavContainer.querySelector("[data-sub-menu-team]");

        const desktopHiddenClass = getNavigationClass("md:hidden", "lg:hidden");
        if (teams?.classList.contains(desktopHiddenClass)) return;

        if (!menu || menu.classList.contains(desktopHiddenClass)) return;

        const TEAM_NAME_MAX_WIDTH = 148;
        const items = menu.querySelector("ul");
        const titles = items.querySelectorAll("a");
        let longestTitleWidth = 0;

        titles.forEach((title) => {
            if (
                title.clientWidth > TEAM_NAME_MAX_WIDTH &&
                title.clientWidth > longestTitleWidth
            ) {
                longestTitleWidth = title.clientWidth;
            }
        });

        if (longestTitleWidth > TEAM_NAME_MAX_WIDTH) {
            titles.forEach((title) => {
                title.style.width = `${longestTitleWidth}px`;
            });
        }

        if (!teams || !isSafari()) return;

        if (menu.scrollHeight - 1 > menu.clientHeight) {
            const contentHeight = menu.scrollHeight;

            menu.style.height = `${contentHeight}px`;
        }
    });
};

const observeTeamsContainerHeight = () => {
    let prevHeight = null;
    const teamsContainers = document.querySelector(
        "#desktopNavigation [data-sub-menu-team]",
    );

    if (!teamsContainers) return;

    const teamsContainerResize = new ResizeObserver((entries) => {
        let rect = entries[0].contentRect;
        let height = rect.height;

        if (prevHeight !== null && height !== prevHeight) {
            setSubNavItemsHeight();
        }

        prevHeight = height;
    });

    if (teamsContainers) {
        teamsContainerResize.observe(teamsContainers);
    }

    setSubNavItemsHeight();
};

const sportDropdownWidthSafari = () => {
    const subNavContainers = document.querySelectorAll(
        "#desktopNavigation [data-has-dropdown]",
    );

    if (!isSafari() || subNavContainers.length === 0) return;

    subNavContainers.forEach((subNavContainer) => {
        const menu = subNavContainer.querySelector("[data-sub-menu]");

        if (!menu) return;

        const container = menu.querySelector("ul");
        const items = container.querySelectorAll("li");
        const twoColumnsSubMenuDropdown = items.length > 10;

        if (twoColumnsSubMenuDropdown) {
            container.style.width = `${container.offsetWidth * 2}px`;
        }
    });
};

const sportSubMenuHoverBold = async () => {
    const activeSubMenuItems = document.querySelectorAll(
        "#desktopSecondaryNavigation > li",
    );
    await preSetHoverBoldText(activeSubMenuItems, "row");
};

export const updateSubNavItemStyles = () => {
    handleNavigationMediaQueryChange(null, sportSubMenuHoverBold);
    handleNavigationMediaQueryChange(null, observeTeamsContainerHeight);
    handleNavigationMediaQueryChange(null, sportDropdownWidthSafari);
};

// Desktop ticker navigation dropdown offset position & hover state
const toggleDropdown = (ticker, tickerNavigation, dropdown, isHovered) => {
    if (isHovered) {
        dropdown.classList.add("visible", "opacity-100");
        dropdown.classList.remove("invisible", "opacity-0");
        ticker.classList.add("text-white-js");
        tickerNavigation.classList.add("grey-text-js");
    } else {
        dropdown.classList.add("invisible", "opacity-0");
        dropdown.classList.remove("visible", "opacity-100");
        ticker.classList.remove("text-white-js");
        tickerNavigation.classList.remove("grey-text-js");
    }
};

const setupTickerHover = (ticker, tickerNavigation, dropdown) => {
    let isTickerHovered = false;

    const handleTickerMouseOver = () => {
        isTickerHovered = true;
        toggleDropdown(ticker, tickerNavigation, dropdown, true);
    };

    const handleTickerMouseOut = (event) => {
        if (
            !ticker.contains(event.relatedTarget) &&
            !dropdown.contains(event.relatedTarget)
        ) {
            isTickerHovered = false;
            toggleDropdown(ticker, tickerNavigation, dropdown, false);
        }
    };

    const handleDropdownMouseOver = () => {
        if (isTickerHovered) {
            toggleDropdown(ticker, tickerNavigation, dropdown, true);
        }
    };

    const handleDropdownMouseOut = (event) => {
        if (isTickerHovered && !ticker.contains(event.relatedTarget)) {
            toggleDropdown(ticker, tickerNavigation, dropdown, false);
        }
    };

    ticker.addEventListener("mouseover", handleTickerMouseOver);
    ticker.addEventListener("mouseout", handleTickerMouseOut);
    dropdown.addEventListener("mouseover", handleDropdownMouseOver);
    dropdown.addEventListener("mouseout", handleDropdownMouseOut);
};

export const nbaTickerHoverHandler = () => {
    const nbaTeamsTicker = document.querySelector("#nbaTeamsTicker");
    const nbaGlobalTicker = document.querySelector("#nbaGlobalTicker");
    const nbaTeamsDropdown = document.querySelector("#nbaTeamsDropdown");
    const nbaGlobalDropdown = document.querySelector("#nbaGlobalDropdown");
    const tickerNavigation = document.querySelector("#tickerNavigation");

    // The navigation ticker is sometimes not present on the page
    // This can happen when is_nba_sib is set to true and page.nba_ticker_menu is empty
    if (!tickerNavigation) {
        return;
    }

    if (nbaTeamsTicker && nbaTeamsDropdown) {
        setupTickerHover(nbaTeamsTicker, tickerNavigation, nbaTeamsDropdown);
    }

    if (nbaGlobalTicker && nbaGlobalDropdown) {
        setupTickerHover(nbaGlobalTicker, tickerNavigation, nbaGlobalDropdown);
    }
};

const alignTickerDropdownToLeft = () => {
    const tickerNav = document.querySelector("#tickerNavigation");

    // The navigation ticker is sometimes not present on the page
    // This can happen when is_nba_sib is set to true and page.nba_ticker_menu is empty
    if (!tickerNav) {
        return;
    }

    const tickerNavRect = tickerNav.getBoundingClientRect();
    const nbaTeamsDropdown = document.querySelector("#nbaTeamsDropdown");
    const nbaGlobalDropdown = document.querySelector("#nbaGlobalDropdown");

    if (!nbaTeamsDropdown || !nbaGlobalDropdown) {
        return;
    }

    nbaTeamsDropdown.style.left = 0;
    nbaTeamsDropdown.style.transform = `translateX(${tickerNavRect.left}px)`;
    nbaGlobalDropdown.style.transform = `translateX(${tickerNavRect.left}px)`;
};

export const updateTickerNavDropdownPosition = () => {
    handleNavigationMediaQueryChange(null, alignTickerDropdownToLeft);
    handleNavigationMediaQueryChange(
        null,
        window.addEventListener("resize", debounce(alignTickerDropdownToLeft)),
    );
};

let overflowViewportWidth = null; // Store the viewport width where overflow was detected
let overflowDetectorInitialized = false;
const desktopOverflowNavDetector = () => {
    const desktopNavigation = document.querySelector("#desktopNavigation");
    const desktopNavigationLogo = document.querySelector(
        "#desktopNavigationLogo",
    );
    const desktopLogoDivider = document.querySelector("#desktopLogoDivider");
    const burgerMenu = document.querySelector("#burgerMenu");
    const mobileEditionPicker = document.querySelector(
        "[data-testid='sidebar--edition-picker']",
    );
    const tickerSocialMediaIcons = document.querySelector(
        "#ticker-social-media",
    );
    const desktopFlexClass = getNavigationClass("md:flex", "lg:flex");
    const desktopHiddenClass = getNavigationClass("md:hidden", "lg:hidden");
    const desktopBlockClass = getNavigationClass("md:block", "lg:block");
    const DESKTOP_NAVIGATION_PADDING = 32;

    if (!desktopNavigation || !desktopNavigationLogo) return;

    let desktopNavigationWidth =
        desktopNavigation.offsetWidth + desktopNavigationLogo.offsetWidth;
    let documentWidth =
        document.documentElement.clientWidth - DESKTOP_NAVIGATION_PADDING;

    if (desktopNavigationWidth > documentWidth) {
        if (documentWidth !== overflowViewportWidth) {
            // Only toggle if it's the first time we detect overflow
            overflowViewportWidth = documentWidth;

            desktopNavigation.classList.remove(desktopFlexClass);
            burgerMenu.classList.remove(desktopHiddenClass);
            desktopLogoDivider?.classList.remove(desktopBlockClass);
            mobileEditionPicker?.classList.add("mobile-edition-picker");
            tickerSocialMediaIcons?.classList.add(desktopHiddenClass);

            // Burger Toggle
            toggleMobileSidebarMenu();

            // Edition Picker
            const editionButtonLocator = "#mobileEditionButton";
            const editionsMenuLocator = "#mobileEditionsMenu";
            const editionButtonArrowLocator = "#mobileEditionButtonArrow";

            toggleEditionMenu(
                editionButtonLocator,
                editionButtonArrowLocator,
                editionsMenuLocator,
            );
        }
    } else {
        if (documentWidth >= overflowViewportWidth) {
            overflowViewportWidth = null;

            desktopNavigation.classList.add(desktopFlexClass);
            burgerMenu.classList.add(desktopHiddenClass);
            desktopLogoDivider?.classList.add(desktopBlockClass);
            mobileEditionPicker?.classList.remove("mobile-edition-picker");
        }
    }
};

export const overflowDetector = () => {
    if (overflowDetectorInitialized) {
        return;
    }
    overflowDetectorInitialized = true;
    const runDetector = () => desktopOverflowNavDetector();
    const scheduleInitialRun = () =>
        window.requestAnimationFrame(() =>
            window.requestAnimationFrame(runDetector),
        );

    if (document?.fonts?.ready) {
        document.fonts.ready.then(scheduleInitialRun).catch(scheduleInitialRun);
    } else if (document.readyState === "complete") {
        scheduleInitialRun();
    } else {
        window.addEventListener("load", scheduleInitialRun, { once: true });
    }

    window.addEventListener("resize", runDetector);
};

export const firefoxaMoreItemsDropdown = () => {
    const moreItemsContainer = document.querySelector(
        "#headerNavigation [data-testid='expandableMenuOnDesktop']",
    );
    const nesn = document.querySelector("[data-skin='skin-nesn.css']");
    const snp = document.querySelector("[data-skin='skin-snp.css']");
    if (!isFirefox || !moreItemsContainer || nesn || snp) return;

    const moreItemsSelector = `li[class*='${getNavigationClass(
        "md:!block",
        "lg:!block",
    )}']`;
    const moreItems = moreItemsContainer.querySelectorAll(moreItemsSelector);

    const itemsCount = moreItems.length;
    const storedBaseWidth = moreItemsContainer.dataset.baseWidth
        ? parseFloat(moreItemsContainer.dataset.baseWidth)
        : null;
    const baseWidth = storedBaseWidth ?? moreItemsContainer.offsetWidth;
    if (!storedBaseWidth) {
        moreItemsContainer.dataset.baseWidth = String(baseWidth);
    }

    const columns = Math.max(1, Math.ceil(itemsCount / 5));
    moreItemsContainer.style.width = `${baseWidth * columns}px`;
};

// Desktop additonal navigation dropdown offset position
const resetAdditionalMenuDropdownInlineLeft = () => {
    const additionalNav = document.querySelector("#desktopAdditionalMenu");
    if (!additionalNav) {
        return;
    }

    additionalNav.querySelectorAll(":scope > li").forEach((item) => {
        const dropdown = item.querySelector("[data-has-dropdown]");
        dropdown?.style.removeProperty("left");
    });
};

const additionalMenuDropdownPlacement = () => {
    const additionalNav = document.querySelector("#desktopAdditionalMenu");
    if (!additionalNav) {
        return;
    }

    resetAdditionalMenuDropdownInlineLeft();

    const additionalNavItems = additionalNav.querySelectorAll(":scope > li");
    const additionalMenuColumns = document.querySelectorAll(
        "[data-additional-sub-menu-columns]",
    );

    if (isFirefox) {
        for (const column of additionalMenuColumns) {
            const itemCount = column.querySelectorAll(
                "[data-dropdown-menu-items]",
            ).length;
            const multiplier = Math.ceil(itemCount / 10);

            column.style.width = `${column.offsetWidth * multiplier}px`;
        }
    }

    const translateClass = getNavigationClass(
        "md:-translate-x-1/2",
        "lg:-translate-x-1/2",
    );
    additionalNavItems.forEach((item) => {
        const dropdown = item.querySelector("[data-has-dropdown]");
        if (!dropdown) {
            return;
        }

        const dropdownRect = dropdown.getBoundingClientRect();
        const additionalNavRect = additionalNav.getBoundingClientRect();

        if (dropdown.classList.contains(translateClass)) {
            const dropdownRectRight = dropdownRect.right;
            const additionalNavRectRight = additionalNavRect.right;

            if (dropdownRectRight > additionalNavRectRight) {
                dropdown.style.left = `calc(50% - ${dropdownRectRight - additionalNavRectRight}px)`;
            } else {
                dropdown.style.left = "50%";
            }
        }
    });
};

let expandedDropdownPositions = [];
let condensedDropdownPositions = [];
window.__doneNavDetection = false;

const getExpandedDropdownsPosition = () => {
    const primaryNav = document.querySelector("#headerNavigation");
    const primaryNavItems = document.querySelectorAll(
        "#desktopPrimaryMenu > [data-menu-item]",
    );

    // Measure the actual logo-width delta so the estimate is correct for both
    // v1 (expanded: max-w-64 = 256px, condensed: max-w-8 = 32px → ~224px diff) and
    // v3 (expanded: max-w-[216.45px], condensed: max-w-12 = 48px → ~168px diff).
    const expandedLogoEl = document.querySelector("#expanded-logo");
    const CONDENSED_LOGO_WIDTH_PX = isNavigationV3() ? 48 : 32; // max-w-12 vs max-w-8
    const EXPANDED_CONDENSED_DIFFERENCE = expandedLogoEl
        ? Math.max(0, expandedLogoEl.offsetWidth - CONDENSED_LOGO_WIDTH_PX)
        : 168;
    const NAVIGATION_PADDINGS = 23;
    const BORDER_WIDTH = 2;
    const extraLeftSpacing =
        (window.innerWidth - primaryNav.offsetWidth) / 2 - NAVIGATION_PADDINGS;

    const pushDropdownPosition = (
        dropdownPositionsArray,
        id,
        position,
        transform,
    ) => {
        dropdownPositionsArray.push({
            id,
            position,
            transform,
        });
    };

    const desktopHiddenClass = getNavigationClass("md:hidden", "lg:hidden");
    primaryNavItems.forEach((item, id) => {
        // Skip if the item is hidden
        if (item.classList.contains(desktopHiddenClass)) return;

        const dropdown = item.querySelector("[data-has-dropdown]");
        if (!dropdown) return; // Skip if no dropdown

        const itemRect = item.getBoundingClientRect();
        const dropdownRect = dropdown.getBoundingClientRect();
        const primaryNavRect = primaryNav.getBoundingClientRect();

        const dropdownLeft = Math.round(dropdownRect.left);
        const primaryNavLeft = Math.round(primaryNavRect.left);
        const itemLeft = Math.round(itemRect.left);

        const expandedPosition = dropdownLeft - primaryNavLeft - BORDER_WIDTH;
        const condensedPosition =
            dropdownLeft -
            EXPANDED_CONDENSED_DIFFERENCE -
            primaryNavLeft -
            BORDER_WIDTH;

        let isDropdownOverlap =
            itemLeft - primaryNavLeft < itemLeft - dropdownLeft;

        // Handle expanded dropdown positions
        if (expandedPosition < primaryNavLeft - extraLeftSpacing) {
            pushDropdownPosition(
                expandedDropdownPositions,
                id,
                isDropdownOverlap ? 0 : "50%",
                isDropdownOverlap,
            );
        } else {
            pushDropdownPosition(expandedDropdownPositions, id, "50%", null);
        }

        // Handle condensed dropdown positions
        if (condensedPosition < primaryNavLeft - extraLeftSpacing) {
            pushDropdownPosition(
                condensedDropdownPositions,
                id,
                isDropdownOverlap ? "50%" : condensedPosition,
                isDropdownOverlap,
            );
        } else {
            pushDropdownPosition(condensedDropdownPositions, id, null, null);
        }
    });
    window.__doneNavDetection = true;
};

const primaryMenuDropdownPlacement = () => {
    const primaryNavItems = document.querySelectorAll(
        "#desktopPrimaryMenu > [data-menu-item]",
    );

    const desktopHiddenClass = getNavigationClass("md:hidden", "lg:hidden");
    primaryNavItems.forEach((item, index) => {
        if (!item.classList.contains(desktopHiddenClass)) {
            const dropdown = item.querySelector("[data-has-dropdown]");
            const expandedLogoImgDiv = document.querySelector("#expanded-logo");
            const condensedLogoImgDiv =
                document.querySelector("#condensed-logo");

            if (
                condensedLogoImgDiv?.classList.contains(
                    "logo-condensed--visible",
                )
            ) {
                const condensedDropdownData = condensedDropdownPositions.find(
                    (obj) => obj.id === index,
                );

                if (condensedDropdownData) {
                    dropdown.style.setProperty(
                        "left",
                        condensedDropdownData.position === 0
                            ? "50%"
                            : `calc(50% - ${condensedDropdownData.position}px)`,
                    );
                    if (condensedDropdownData.transform) {
                        dropdown.style.setProperty("transform", `none`);
                        dropdown.style.setProperty("left", 0);
                        dropdown.style.setProperty("translate", "unset");
                        item.style.setProperty("position", `static`);
                    }
                }
            }

            if (
                !condensedLogoImgDiv ||
                expandedLogoImgDiv?.classList.contains("logo-expanded--visible")
            ) {
                const expandedDropdownData = expandedDropdownPositions.find(
                    (obj) => obj.id === index,
                );
                if (expandedDropdownData) {
                    dropdown.style.setProperty(
                        "left",
                        expandedDropdownData.position,
                    );

                    if (expandedDropdownData.transform) {
                        dropdown.style.setProperty("transform", `none`);
                        dropdown.style.setProperty("translate", "unset");
                        item.style.setProperty("position", `static`);
                    }
                }
            }
        }
    });
};

const BOLD_FONT_WIDTH_ALLOWANCE = 2;

const preSetHoverBoldText = async (items, direction) => {
    if (items.length === 0) return;

    await document.fonts.ready;

    if (direction === "column") {
        Array.from(items).forEach((item) => {
            const links = item.querySelectorAll("[data-dropdown-menu-items]");
            if (links.length) {
                const naturalWidth = links[0].clientWidth;
                links.forEach((el) => {
                    el.style.width = `${naturalWidth + BOLD_FONT_WIDTH_ALLOWANCE}px`;
                });
            }
        });
    }

    if (direction === "row") {
        items.forEach((item) => {
            item.style.width = `${item.offsetWidth + BOLD_FONT_WIDTH_ALLOWANCE}px`;

            if (item.classList.contains("mr-0.5")) {
                item.classList.remove("mr-0.5");
            }
        });
    }
};

const resetPreSetHoverBoldText = async (items) => {
    if (items.length === 0) return;

    await document.fonts.ready;

    items.forEach((item) => {
        const allLinks = item.querySelectorAll("[data-dropdown-menu-items]");

        allLinks.forEach((link) => {
            link.style.removeProperty("width");
            link.style.width = "";
        });
    });
};

const checkTeamName = (teams) => {
    let isTeamNameWrapped = false;
    if (teams) {
        teams.forEach((team) => {
            team.querySelectorAll("[data-team-title]").forEach((title) => {
                const city = title.children[0];
                const name = title.children[title.children.length - 1];

                if (
                    city.getBoundingClientRect().top !==
                    name.getBoundingClientRect().top
                ) {
                    isTeamNameWrapped = true;
                }
            });
        });
    }

    if (isTeamNameWrapped) {
        teams.forEach((team) => {
            team.querySelectorAll("[data-team-title]").forEach((title) => {
                const city = title.children[0];
                city.style.display = "none";
            });
        });
    }
};

const getTeamsDivision = () => {
    const dropdownWithTeams = document.querySelectorAll(
        "[data-has-dropdown] [data-sub-menu-team]",
    );

    dropdownWithTeams.forEach(async (teamDropdown) => {
        const division = teamDropdown.querySelector("[data-teams-division]");
        if (!division) return;

        const teams = division.querySelectorAll("[data-team]");
        const dropdown = teamDropdown.closest("[data-has-dropdown]");
        const menu = dropdown.closest("[data-menu-item]");

        const relativeClass = getNavigationClass("md:relative", "lg:relative");
        switch (teams.length) {
            case 1:
                division.classList.add("js-grid-cols-1");
                dropdown.classList.add("js-dropdown-centered");
                menu.classList.add(relativeClass);
                break;
            case 2:
                division.classList.add("js-grid-cols-2");
                dropdown.classList.add("js-dropdown-centered");
                menu.classList.add(relativeClass);
                break;
            case 3:
                division.classList.add("js-grid-cols-3");
                dropdown.classList.add("js-dropdown-centered");
                menu.classList.add(relativeClass);
                break;
            default: {
                const threeColumnSports = dropdown.querySelectorAll(
                    "a[href*='/nba'], a[href*='/mlb'], a[href*='/soccer']",
                );

                if (threeColumnSports.length > 0) {
                    threeColumnSports.forEach((dropdown) => {
                        dropdown.classList.add("js-dropdown-centered");
                        division.classList.add("js-grid-cols-3");
                        menu.classList.add("js-relative-three-column");
                    });
                } else {
                    division.classList.add("js-grid-cols-4");
                }
                break;
            }
        }

        checkTeamName(teams);
        await preSetHoverBoldText(teams, "column");
    });
};

const markNavDropdownsReady = () => {
    document
        .querySelector("#mainNavigation[data-navigation-version='v3']")
        ?.setAttribute("data-nav-dropdowns-ready", "");
};

const runDesktopPrimaryDropdownLayout = () => {
    getTeamsDivision();
    getExpandedDropdownsPosition();
    primaryMenuDropdownPlacement();
    additionalMenuDropdownPlacement();
    markNavDropdownsReady();
};

export const updatePrimaryNavDropdownPosition = async () => {
    await sportMenusHoverHandler();

    handleNavigationMediaQueryChange(
        resetAdditionalMenuDropdownInlineLeft,
        runDesktopPrimaryDropdownLayout,
    );

    handleNavigationMediaQueryChange(
        null,
        monitorForChanges("#tsnLogo", primaryMenuDropdownPlacement),
    );
};

export const secondaryMenuDropdown = () => {
    let state = {
        dropdown: null,
        menu: null,
        chevron: null,
    };

    const menuHasDropdowns = document.querySelectorAll(
        "[data-secondary-menu-navigation] [data-menu-has-dropdown]",
    );
    if (!menuHasDropdowns.length) return;

    preSetHoverBoldText(menuHasDropdowns, "column");
    menuHasDropdowns.forEach((menu) => {
        const dropdown = menu.querySelector("[data-has-dropdown]");
        const chevron = menu.querySelector("[data-menu-chevron]");
        const secondaryNav = document.querySelector(
            "[data-secondary-menu-navigation]",
        );
        if (!dropdown) return;

        menu.addEventListener("click", (event) => {
            event.stopPropagation();

            const willOpen = dropdown.classList.contains("opacity-0");

            // Close any previously active dropdown
            if (state.dropdown && state.dropdown !== dropdown) {
                resetSecondaryMenuDropdown(state);
            }

            // If the secondary nav has no horizontal scroll, remove overflow classes.
            // If it has horizontal scroll and the dropdown is about to open, recalculate the dropdown position so it follows the correct horizontal offset.
            if (!hasHorizontalScroll(secondaryNav)) {
                secondaryNav.classList.remove(
                    "overflow-x-auto",
                    "overflow-y-hidden",
                );
            } else if (willOpen && secondaryNav.classList.contains("sub-nav")) {
                attachDropdownPositioning(menu, dropdown);
            }

            // Always hide ellipsis dropdown when opening another dropdown
            hideEllipsisDropdownSubnav();

            // Toggle current dropdown
            menu.classList.toggle("font-bold", willOpen);
            dropdown.classList.toggle("opacity-0", !willOpen);
            dropdown.classList.toggle("pointer-events-none", !willOpen);
            chevron.classList.toggle("rotate-180", !willOpen);

            // Update State
            Object.assign(
                state,
                willOpen
                    ? { dropdown, menu, chevron }
                    : { dropdown: null, menu: null, chevron: null },
            );
        });
    });

    // Close on outside click
    document.addEventListener("click", () => {
        if (state.dropdown) resetSecondaryMenuDropdown(state);
    });

    const secondaryNav = document.querySelector(
        "[data-secondary-menu-navigation]",
    );

    // Close dropdown if user scrolled horizontally on subnav
    secondaryNav.addEventListener("scroll", (e) => {
        if (
            e.deltaX !== 0 &&
            secondaryNav.classList.contains("sub-nav") &&
            window.innerWidth >= getNavigationBreakpoint()
        ) {
            if (state.dropdown) resetSecondaryMenuDropdown(state);
        }
    });

    // Close on hover into main menu
    document
        .querySelectorAll("#mainNavigation [data-menu-has-dropdown]")
        .forEach((menu) => {
            menu.addEventListener("mouseenter", () => {
                if (state.dropdown) resetSecondaryMenuDropdown(state);
            });
        });
};

export const updateSecondaryNavDropdownPosition = async () => {
    const menuHasDropdowns = document.querySelectorAll(
        "[data-secondary-menu-navigation] [data-menu-has-dropdown]",
    );
    if (!menuHasDropdowns.length) return;

    await preSetHoverBoldText(menuHasDropdowns, "column");

    handleNavigationMediaQueryChange(
        mobileSecondaryMenuDropdown(),
        secondaryMenuDropdownPlacement(),
    );

    handleNavigationMediaQueryChange(
        window.addEventListener("resize", debounce(shrinkFontWithLongTitle)),
        window.addEventListener(
            "resize",
            debounce(secondaryMenuDropdownPlacement),
        ),
    );
};

async function shrinkFontWithLongTitle() {
    const subNavContainers = document.querySelectorAll(
        "#mobileSecondaryNavigation [data-has-dropdown]",
    );
    const SINGLE_LINE_HEIGHT = 18;

    if (subNavContainers.length < 1) return;

    await document.fonts.ready;

    subNavContainers.forEach((subNavContainer) => {
        const menuItems = subNavContainer.querySelectorAll(
            "[data-dropdown-menu-items] .text-nav-secondary",
        );
        const anyTruncated = Array.from(menuItems).some(
            (item) => item.scrollHeight > SINGLE_LINE_HEIGHT,
        );

        if (anyTruncated) {
            menuItems.forEach((item) => {
                item.style.fontSize = "10px";
            });
        }
    });
}

function mobileSecondaryMenuDropdown() {
    const menuHasDropdowns = document.querySelectorAll(
        "#mobileSecondaryNavigation[data-secondary-menu-navigation] [data-menu-has-dropdown]",
    );
    if (!menuHasDropdowns.length) return;

    resetPreSetHoverBoldText(menuHasDropdowns);

    shrinkFontWithLongTitle();
}

function secondaryMenuDropdownPlacement() {
    const secondaryNav = document.querySelector(
        "#desktopSecondaryNavigation[data-secondary-menu-navigation]",
    );
    if (!secondaryNav) return;

    const menuHasDropdowns = secondaryNav.querySelectorAll(
        "[data-menu-has-dropdown]",
    );
    if (!menuHasDropdowns.length) return;

    const leftClass = getNavigationClass("md:-left-3", "lg:-left-3");
    const rightClass = getNavigationClass("md:right-0", "lg:right-0");
    menuHasDropdowns.forEach((menu) => {
        const dropdown = menu.querySelector("[data-has-dropdown]");
        if (!dropdown) return;

        const dropdownRectRight = dropdown.getBoundingClientRect().right;
        const secondaryNavRectRight =
            secondaryNav.getBoundingClientRect().right;

        if (dropdownRectRight > secondaryNavRectRight) {
            dropdown.classList.replace(leftClass, rightClass);
            if (!isNavigationV3()) {
                menu.classList.remove("relative");
            }
        }
    });
}

function resetSecondaryMenuDropdown(state) {
    state.menu?.classList.remove("font-bold");
    state.dropdown?.classList.add("opacity-0", "pointer-events-none");
    state.chevron?.classList.add("rotate-180");

    state.menu = null;
    state.dropdown = null;
    state.chevron = null;
}

/**
 * Enable click-to-open behavior for desktop dropdowns on devices that cannot hover.
 */
export const enableDesktopClickDropdownsOnNoHover = () => {
    const SELECTOR_PRIMARY_ITEMS = "#desktopPrimaryMenu > [data-menu-item]";
    const SELECTOR_ADDITIONAL_ITEMS = "#desktopAdditionalMenu > li";
    const SELECTOR_TEXT = "a, button";
    const SELECTOR_DROPDOWN = "[data-has-dropdown]";

    const hoverMediaQuery = window.matchMedia(
        "(hover: hover) and (pointer: fine)",
    );

    let openDropdownState = null;
    let menuTexts = [];

    const clearTextStyles = () => {
        if (!menuTexts || menuTexts.length === 0) return;
        menuTexts.forEach((el) => {
            el.classList.remove("text-white", "text-d1");
        });
    };

    const getTextsFor = (menuItems) =>
        menuItems
            .map((item) => item.querySelector(SELECTOR_TEXT))
            .filter(Boolean);

    const setActiveText = (selectedText) => {
        if (!menuTexts || menuTexts.length === 0) return;
        menuTexts.forEach((el) => {
            const isSelected = el === selectedText;
            el.classList.toggle("text-white", isSelected);
            el.classList.toggle("text-d1", !isSelected);
        });
    };

    const closeCurrentDropdown = () => {
        clearTextStyles();
        if (!openDropdownState) return;
        const { dropdown } = openDropdownState;
        hideDropdownPanel(dropdown);
        openDropdownState = null;
    };

    const hideDropdownPanel = (panelEl) => {
        if (!panelEl) return;
        panelEl.style.removeProperty("visibility");
        panelEl.style.removeProperty("opacity");
        panelEl.style.removeProperty("pointer-events");
    };

    const showDropdownPanel = (panelEl) => {
        if (!panelEl) return;
        panelEl.style.visibility = "visible";
        panelEl.style.opacity = "1";
        panelEl.style.pointerEvents = "auto";
    };

    const bindClickDropdowns = () => {
        const canHover = hoverMediaQuery.matches;
        if (canHover) return;

        const primaryMenuItems = document.querySelectorAll(
            SELECTOR_PRIMARY_ITEMS,
        );
        const additionalMenuItems = document.querySelectorAll(
            SELECTOR_ADDITIONAL_ITEMS,
        );

        const menuItems = [...primaryMenuItems, ...additionalMenuItems];
        if (menuItems.length === 0) return;

        menuTexts = getTextsFor(menuItems);

        const onDocumentClickOutside = (e) => {
            if (!openDropdownState) return;
            const { item } = openDropdownState;
            if (!item.contains(e.target)) {
                closeCurrentDropdown();
            }
        };

        document.addEventListener("click", onDocumentClickOutside);

        menuItems.forEach((item) => {
            const dropdownPanel = item.querySelector(SELECTOR_DROPDOWN);
            const textEl = item.querySelector(SELECTOR_TEXT);

            if (!dropdownPanel || !textEl) return;

            const onTextClick = (e) => {
                if (!openDropdownState || openDropdownState.item !== item) {
                    e.preventDefault();
                    closeCurrentDropdown();
                    setActiveText(textEl);
                    showDropdownPanel(dropdownPanel);
                    openDropdownState = { item, dropdown: dropdownPanel };
                } else {
                    closeCurrentDropdown();
                }
            };

            textEl.addEventListener("click", onTextClick);
        });
    };

    hoverMediaQuery.addEventListener?.("change", bindClickDropdowns);
    bindClickDropdowns();
};

// Positions the dropdown relative to its menu button.
// A small adjustment is added when using the ellipsis dropdown.
function attachDropdownPositioning(menu, dropdown) {
    const rect = menu.getBoundingClientRect();
    let baseTop = rect.top + rect.height;
    const baseLeft = rect.left;

    dropdown.style.position = "fixed";
    dropdown.style.top = `${baseTop}px`;
    dropdown.style.left = `${baseLeft}px`;

    let openedAtTop = window.scrollY === 0;
    let ticking = false;

    const onScroll = () => {
        if (!dropdown || dropdown.style.display === "none") return;
        if (ticking) return;

        ticking = true;

        requestAnimationFrame(() => {
            const isAtTop = window.scrollY === 0;

            if (isAtTop) {
                const newRect = menu.getBoundingClientRect();
                baseTop = newRect.top + newRect.height;

                dropdown.style.top = `${baseTop}px`;

                openedAtTop = true;
            } else if (openedAtTop) {
                const offset = Math.min(window.scrollY, 32);

                dropdown.style.top = `${baseTop - offset}px`;
            }

            ticking = false;
        });
    };

    window.addEventListener("scroll", onScroll, { passive: true });
}

// Checks if the element has horizontal scroll.
function hasHorizontalScroll(el) {
    if (!el) return false;
    return el.scrollWidth > el.clientWidth;
}

// Hides the ellipsis dropdown instantly.
function hideEllipsisDropdownSubnav() {
    const dropdown = document.querySelector(".hover-sub-nav div:has(ul)");
    if (!dropdown) return;
    const visibleClass = getNavigationClass("md:visible", "lg:visible");
    const opacityClass = getNavigationClass("md:opacity-100", "lg:opacity-100");
    dropdown.classList.remove(visibleClass);
    dropdown.classList.remove(opacityClass);
}

export const enableClickEllipsisDropdownSubnav = () => {
    const hoverableItemSubNav = document.querySelector(".hover-sub-nav");
    const hoverableSubnavDropdown = document.querySelector(
        ".hover-sub-nav div:has(ul)",
    );
    const secondaryNav = document.querySelector(
        "[data-secondary-menu-navigation]",
    );
    const visibleClass = getNavigationClass("md:visible", "lg:visible");
    const opacityClass = getNavigationClass("md:opacity-100", "lg:opacity-100");

    const showEllipsisDropdown = () => {
        if (!hoverableSubnavDropdown) return;
        hoverableSubnavDropdown.classList.add(visibleClass);
        hoverableSubnavDropdown.classList.add(opacityClass);
    };

    // Checks if element is at least partially inside viewport.
    const isPartiallyOrFullyOutOfViewport = (el) => {
        if (!el) return false;
        const rect = el.getBoundingClientRect();

        return (
            rect.bottom > 0 &&
            rect.right > 0 &&
            rect.top < window.innerHeight &&
            rect.left < window.innerWidth
        );
    };

    // Touchscreen or horizontal scroll mode.
    const isTouchscreen = window.matchMedia("(pointer: coarse)").matches;

    // Add click event on touch device or subnav with scrollbar
    if (hasHorizontalScroll(secondaryNav) || isTouchscreen) {
        hoverableItemSubNav.addEventListener("click", (event) => {
            event.stopPropagation();
            showEllipsisDropdown();
        });

        document.addEventListener("click", () => {
            hideEllipsisDropdownSubnav();
        });

        // Close ellipsis dropdown if user scrolled horizontally
        secondaryNav.addEventListener("scroll", (e) => {
            if (e.deltaX !== 0 && secondaryNav.classList.contains("sub-nav")) {
                hideEllipsisDropdownSubnav();
            }
        });
    }

    window.onload = () => {
        requestAnimationFrame(() => {
            const subnavHasScroll = hasHorizontalScroll(secondaryNav);
            const dropdownOutOfView = isPartiallyOrFullyOutOfViewport(
                hoverableSubnavDropdown,
            );
            if (
                !hoverableItemSubNav ||
                !hoverableSubnavDropdown ||
                !secondaryNav
            )
                return;
            // If the user is using a touch device and inside a scrollbar subnav, use dropdown recalculation else remove overflow and proceed with the existing hover positioning functionality
            if (subnavHasScroll) {
                attachDropdownPositioning(
                    hoverableItemSubNav,
                    hoverableSubnavDropdown,
                );
            } else {
                hoverableItemSubNav.addEventListener("mouseover", () => {
                    secondaryNav.classList.remove(
                        "overflow-x-auto",
                        "overflow-y-hidden",
                    );
                });
            }

            // Position the ellipsis dropdown to the right if the dropdown is not shown fully initially
            if (dropdownOutOfView && subnavHasScroll) {
                hoverableSubnavDropdown.style.left = "unset";
                hoverableSubnavDropdown.style.right = "8px";
                hoverableSubnavDropdown.style.translate = "none";
            }
        });
    };
};
